import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Navigate } from 'react-router-dom';
import { getCurrentUser } from 'aws-amplify/auth';
import {
  ReactFlow,
  ReactFlowProvider,
  MiniMap,
  Controls,
  Background,
  BackgroundVariant,
  useNodesState,
  useEdgesState,
  useReactFlow,
  addEdge,
  type Node,
  type Edge,
  type Connection,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { ArrowLeft, Save, Loader2, Check, Play } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ThemeToggle } from '../theme/theme-toggle';
import { flowTemplates } from '../../config/flowTemplates';
import { FlowProvider } from './FlowContext';
import { NodePalette } from './NodePalette';
import { categoryColors, nodeTypeToCategory } from '../../config/nodePalette';
import { flowService } from '../../services/flowService';
import { SavedFlow, SerializedNode, SerializedEdge } from '../../types/flows';
import { DemoOverlay } from '../demo/DemoOverlay';
import { hasScriptForTemplate, getScriptIdForTemplate } from '../demo/demoScripts';
import { SignupPrompt } from '../auth/SignupPrompt';

// Node components
import { UploadNode } from './nodes/UploadNode';
import { UploadMediaNode } from './nodes/UploadMediaNode';
import { UploadPdfNode } from './nodes/UploadPdfNode';
import { UrlDownloadNode } from './nodes/UrlDownloadNode';
import { CloudTransferNode } from './nodes/CloudTransferNode';
import { SqlImportNode } from './nodes/SqlImportNode';
import { AutoProcessNode } from './nodes/AutoProcessNode';
import { FilePickerNode } from './nodes/FilePickerNode';
import { FilterNode } from './nodes/FilterNode';
import { SheetsNode } from './nodes/SheetsNode';
import { SearchNode } from './nodes/SearchNode';
import { QueryNode } from './nodes/QueryNode';
import { ChatNode } from './nodes/ChatNode';
import { VisualizeNode } from './nodes/VisualizeNode';
import { ApiEndpointNode } from './nodes/ApiEndpointNode';
import { McpNode } from './nodes/McpNode';
import { ZapierNode } from './nodes/ZapierNode';
import { ExportNode } from './nodes/ExportNode';

const nodeTypes = {
  'upload': UploadNode,
  'upload-data': UploadNode,
  'upload-media': UploadMediaNode,
  'upload-pdf': UploadPdfNode,
  'url-download': UrlDownloadNode,
  'cloud-transfer': CloudTransferNode,
  'sql-import': SqlImportNode,
  'auto-process': AutoProcessNode,
  'file-picker': FilePickerNode,
  'filter': FilterNode,
  'sheets': SheetsNode,
  'search': SearchNode,
  'query': QueryNode,
  'chat': ChatNode,
  'visualize': VisualizeNode,
  'api-endpoint': ApiEndpointNode,
  'mcp': McpNode,
  'zapier': ZapierNode,
  'export': ExportNode,
};

const nodeWidths: Record<string, number> = {
  'upload': 300,
  'upload-data': 300,
  'upload-media': 300,
  'upload-pdf': 300,
  'url-download': 300,
  'cloud-transfer': 340,
  'sql-import': 340,
  'auto-process': 280,
  'file-picker': 300,
  'filter': 350,
  'sheets': 450,
  'search': 350,
  'query': 400,
  'chat': 350,
  'visualize': 320,
  'api-endpoint': 400,
  'mcp': 400,
  'zapier': 350,
  'export': 320,
};

let nodeIdCounter = 0;
const getNextId = () => `user-node-${++nodeIdCounter}`;

const getCategoryColor = (type: string): string => {
  const category = nodeTypeToCategory[type];
  return category ? categoryColors[category] : '#6b7280';
};

function serializeNodes(nodes: Node[]): SerializedNode[] {
  return nodes.map((n) => ({
    id: n.id,
    type: n.type || 'default',
    position: n.position,
    data: {
      label: n.data?.label,
      color: n.data?.color,
      isTemplate: n.data?.isTemplate,
      selectedFile: n.data?.selectedFile || null,
    },
    deletable: n.deletable,
  }));
}

function serializeEdges(edges: Edge[]): SerializedEdge[] {
  return edges.map((e) => ({
    id: e.id,
    source: e.source,
    target: e.target,
    type: e.type,
    animated: e.animated,
    style: e.style as Record<string, any>,
  }));
}

/** Shared canvas component used by both template and saved flow modes */
const FlowCanvas: React.FC<{
  nodes: Node[];
  edges: Edge[];
  onNodesChange: any;
  onEdgesChange: any;
  onConnect: (c: Connection) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  onAddNode: (type: string, label: string) => void;
  topBar: React.ReactNode;
  demoActive?: boolean;
}> = ({ nodes, edges, onNodesChange, onEdgesChange, onConnect, onDragOver, onDrop, onAddNode, topBar, demoActive }) => {
  return (
    <div className="h-screen flex flex-col bg-background overflow-hidden">
      <div
        className="transition-all duration-700 ease-in-out overflow-hidden"
        style={{
          maxHeight: demoActive ? 0 : 60,
          opacity: demoActive ? 0 : 1,
          transform: demoActive ? 'translateY(-100%)' : 'translateY(0)',
        }}
      >
        {topBar}
      </div>
      <div className="flex-1 flex min-h-0">
        <div
          className="transition-all duration-700 ease-in-out overflow-hidden"
          style={{
            maxWidth: demoActive ? 0 : 220,
            opacity: demoActive ? 0 : 1,
            transform: demoActive ? 'translateX(-100%)' : 'translateX(0)',
          }}
        >
          <NodePalette onAddNode={onAddNode} />
        </div>
        <div className="flex-1">
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onDragOver={onDragOver}
            onDrop={onDrop}
            nodeTypes={nodeTypes}
            deleteKeyCode={['Backspace', 'Delete']}
            fitView
            fitViewOptions={{ padding: 0.3 }}
            minZoom={0.3}
            maxZoom={1.5}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant={BackgroundVariant.Dots} gap={20} size={1} />
            <div
              className="transition-opacity duration-700 ease-in-out"
              style={{ opacity: demoActive ? 0 : 1, pointerEvents: demoActive ? 'none' : 'auto' }}
            >
              <MiniMap nodeStrokeWidth={3} zoomable pannable className="!bg-muted/50" />
              <Controls className="!bg-card !border !border-border !shadow-sm [&>button]:!bg-card [&>button]:!border-border [&>button]:!fill-foreground [&>button:hover]:!bg-muted" />
            </div>
          </ReactFlow>
        </div>
      </div>
    </div>
  );
};

/** Hook for shared canvas interactions */
function useCanvasActions(
  nodes: Node[],
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>,
  setEdges: React.Dispatch<React.SetStateAction<any[]>>,
) {
  const { screenToFlowPosition, fitView } = useReactFlow();

  const onConnect = useCallback(
    (connection: Connection) => {
      setEdges((eds) =>
        addEdge(
          { ...connection, type: 'smoothstep', animated: true, style: { stroke: '#9ca3af', strokeWidth: 2 } },
          eds,
        ),
      );
    },
    [setEdges],
  );

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const raw = e.dataTransfer.getData('application/reactflow');
      if (!raw) return;
      try {
        const { type, label } = JSON.parse(raw);
        const position = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const color = getCategoryColor(type);
        const newNode: Node = {
          id: getNextId(),
          type,
          position,
          deletable: true,
          data: { label, color, isTemplate: false },
        };
        setNodes((nds) => [...nds, newNode]);
      } catch {
        // ignore
      }
    },
    [screenToFlowPosition, setNodes],
  );

  const onAddNode = useCallback(
    (type: string, label: string) => {
      const maxX = nodes.reduce((max, n) => {
        const w = nodeWidths[n.type || ''] || 300;
        return Math.max(max, n.position.x + w);
      }, 0);
      const color = getCategoryColor(type);
      const newNode: Node = {
        id: getNextId(),
        type,
        position: { x: maxX + 80, y: 200 },
        deletable: true,
        data: { label, color, isTemplate: false },
      };
      setNodes((nds) => [...nds, newNode]);
      setTimeout(() => fitView({ padding: 0.3 }), 100);
    },
    [nodes, setNodes, fitView],
  );

  return { onConnect, onDragOver, onDrop, onAddNode };
}

/** Template flow — existing behavior + save-as-new button + demo overlay */
const FlowDetailInner: React.FC<{ templateId: string }> = ({ templateId }) => {
  const navigate = useNavigate();
  const template = flowTemplates.find((t) => t.id === templateId)!;
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const [showDemo, setShowDemo] = useState(false);
  const [showSignup, setShowSignup] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(true); // assume true to avoid flash
  const demoScriptId = getScriptIdForTemplate(templateId);
  const hasDemoScript = !!demoScriptId;

  useEffect(() => {
    getCurrentUser().catch(() => setIsAuthenticated(false));
  }, []);

  // Auto-start demo on demo site
  useEffect(() => {
    if (
      hasDemoScript &&
      !showDemo &&
      window.location.hostname === 'demo.bigsplicer.com'
    ) {
      setShowDemo(true);
    }
  }, [hasDemoScript]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialNodes = useMemo(() => {
    const gap = 80;
    let xPos = 40;
    return template.steps.map((step) => {
      const node: Node = {
        id: step.id,
        type: step.type,
        position: { x: xPos, y: 200 },
        deletable: false,
        data: { label: step.label, color: getCategoryColor(step.type), isTemplate: true },
      };
      xPos += (nodeWidths[step.type] || 300) + gap;
      return node;
    });
  }, [template]);

  const initialEdges = useMemo(() => {
    return template.steps.slice(0, -1).map((step, i) => ({
      id: `e-${step.id}-${template.steps[i + 1].id}`,
      source: step.id,
      target: template.steps[i + 1].id,
      type: 'smoothstep' as const,
      animated: true,
      style: { stroke: '#9ca3af', strokeWidth: 2 },
    }));
  }, [template]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { onConnect, onDragOver, onDrop, onAddNode } = useCanvasActions(nodes, setNodes, setEdges);

  const handleSave = async () => {
    if (!isAuthenticated) { setShowSignup(true); return; }
    const name = window.prompt('Save flow as:', template.name);
    if (!name?.trim()) return;
    setSaving(true);
    try {
      const flow = await flowService.createFlow({
        name: name.trim(),
        nodes: serializeNodes(nodes),
        edges: serializeEdges(edges),
        color: template.color,
        color_hex: template.colorHex,
        template_id: template.id,
      });
      navigate(`/flows/${flow.flow_id}`, { replace: true });
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const topBar = (
    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-b bg-background/95 backdrop-blur z-10 shrink-0">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate('/flows')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div
        className="h-6 w-6 rounded flex items-center justify-center shrink-0"
        style={{ background: template.colorHex }}
      >
        <span className="text-white text-xs font-bold">{template.name.charAt(0)}</span>
      </div>
      <div className="flex-1 min-w-0">
        <h2 className="text-sm font-semibold leading-tight truncate">{template.name}</h2>
        <p className="text-[11px] text-muted-foreground hidden sm:block">{template.description}</p>
      </div>
      {hasDemoScript && (
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-8 shrink-0 border-emerald-500/30 text-emerald-600 hover:bg-emerald-50 dark:text-emerald-400 dark:hover:bg-emerald-500/10"
          onClick={() => setShowDemo(true)}
        >
          <Play className="h-3.5 w-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Demo</span>
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 shrink-0"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? <Loader2 className="h-3.5 w-3.5 sm:mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 sm:mr-1.5" />}
        <span className="hidden sm:inline">Save</span>
      </Button>
      <ThemeToggle />
    </div>
  );

  return (
    <>
      <FlowCanvas
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={onConnect}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onAddNode={onAddNode}
        topBar={topBar}
        demoActive={showDemo}
      />
      {demoScriptId && (
        <DemoOverlay
          scriptId={demoScriptId}
          enabled={showDemo}
          onComplete={() => setShowDemo(false)}
        />
      )}
      <SignupPrompt open={showSignup} onOpenChange={setShowSignup} />
    </>
  );
};

/** Auth gate for saved flows — redirects to login if not authenticated */
const RequireAuthForSavedFlow: React.FC<{ flowId: string }> = ({ flowId }) => {
  const [authState, setAuthState] = useState<'checking' | 'authenticated' | 'unauthenticated'>('checking');

  useEffect(() => {
    getCurrentUser()
      .then(() => setAuthState('authenticated'))
      .catch(() => setAuthState('unauthenticated'));
  }, []);

  if (authState === 'checking') {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (authState === 'unauthenticated') {
    return <Navigate to="/login" replace />;
  }

  return (
    <FlowProvider flowColor="blue" flowColorHex="#3b82f6">
      <ReactFlowProvider>
        <SavedFlowDetailInner flowId={flowId} />
      </ReactFlowProvider>
    </FlowProvider>
  );
};

/** Saved flow — loads from API, supports save + rename */
const SavedFlowDetailInner: React.FC<{ flowId: string }> = ({ flowId }) => {
  const navigate = useNavigate();
  const [flow, setFlow] = useState<SavedFlow | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [flowName, setFlowName] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saved'>('idle');
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [nodes, setNodes, onNodesChange] = useNodesState([] as Node[]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([] as Edge[]);
  const { onConnect, onDragOver, onDrop, onAddNode } = useCanvasActions(nodes, setNodes, setEdges);

  useEffect(() => {
    flowService
      .getFlow(flowId)
      .then((f) => {
        setFlow(f);
        setFlowName(f.name);
        // Restore nodes with proper edge styles
        const restoredNodes: Node[] = (f.nodes || []).map((n) => ({
          id: n.id,
          type: n.type,
          position: n.position,
          deletable: n.deletable ?? true,
          data: n.data || {},
        }));
        const restoredEdges: Edge[] = (f.edges || []).map((e) => ({
          id: e.id,
          source: e.source,
          target: e.target,
          type: e.type || 'smoothstep',
          animated: e.animated ?? true,
          style: e.style || { stroke: '#9ca3af', strokeWidth: 2 },
        }));
        setNodes(restoredNodes);
        setEdges(restoredEdges);
        // Reset node ID counter to avoid collisions
        const maxNum = restoredNodes.reduce((max, n) => {
          const m = n.id.match(/^user-node-(\d+)$/);
          return m ? Math.max(max, parseInt(m[1])) : max;
        }, 0);
        nodeIdCounter = Math.max(nodeIdCounter, maxNum);
      })
      .catch((e) => setError(e.message || 'Failed to load flow'))
      .finally(() => setLoading(false));
  }, [flowId, setNodes, setEdges]);

  useEffect(() => {
    if (editingName && nameInputRef.current) {
      nameInputRef.current.focus();
      nameInputRef.current.select();
    }
  }, [editingName]);

  const handleSave = async () => {
    setSaving(true);
    setSaveStatus('idle');
    try {
      await flowService.updateFlow(flowId, {
        name: flowName,
        nodes: serializeNodes(nodes),
        edges: serializeEdges(edges),
      });
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch {
      // ignore
    } finally {
      setSaving(false);
    }
  };

  const handleNameBlur = async () => {
    setEditingName(false);
    if (flowName.trim() && flowName !== flow?.name) {
      try {
        await flowService.updateFlow(flowId, { name: flowName.trim() });
      } catch {
        // revert
        if (flow) setFlowName(flow.name);
      }
    } else if (!flowName.trim() && flow) {
      setFlowName(flow.name);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error || !flow) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-background gap-3">
        <p className="text-muted-foreground">{error || 'Flow not found'}</p>
        <Button variant="outline" size="sm" onClick={() => navigate('/flows')}>
          Back to Flows
        </Button>
      </div>
    );
  }

  const topBar = (
    <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 border-b bg-background/95 backdrop-blur z-10 shrink-0">
      <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0" onClick={() => navigate('/flows')}>
        <ArrowLeft className="h-4 w-4" />
      </Button>
      <div
        className="h-6 w-6 rounded flex items-center justify-center shrink-0"
        style={{ background: flow.color_hex }}
      >
        <span className="text-white text-xs font-bold">{flowName.charAt(0).toUpperCase()}</span>
      </div>
      <div className="flex-1 min-w-0">
        {editingName ? (
          <Input
            ref={nameInputRef}
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            onBlur={handleNameBlur}
            onKeyDown={(e) => { if (e.key === 'Enter') handleNameBlur(); if (e.key === 'Escape') { setFlowName(flow.name); setEditingName(false); } }}
            className="h-7 text-sm font-semibold px-1.5 w-full max-w-xs"
          />
        ) : (
          <h2
            className="text-sm font-semibold leading-tight cursor-pointer hover:text-primary truncate"
            onClick={() => setEditingName(true)}
            title="Click to rename"
          >
            {flowName}
          </h2>
        )}
      </div>
      <Button
        variant="outline"
        size="sm"
        className="text-xs h-8 shrink-0"
        onClick={handleSave}
        disabled={saving}
      >
        {saving ? (
          <Loader2 className="h-3.5 w-3.5 sm:mr-1.5 animate-spin" />
        ) : saveStatus === 'saved' ? (
          <Check className="h-3.5 w-3.5 sm:mr-1.5 text-green-500" />
        ) : (
          <Save className="h-3.5 w-3.5 sm:mr-1.5" />
        )}
        <span className="hidden sm:inline">{saveStatus === 'saved' ? 'Saved' : 'Save'}</span>
      </Button>
      <ThemeToggle />
    </div>
  );

  return (
    <FlowCanvas
      nodes={nodes}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onAddNode={onAddNode}
      topBar={topBar}
    />
  );
};

export const FlowDetailPage: React.FC = () => {
  const { flowId } = useParams<{ flowId: string }>();
  const template = flowTemplates.find((t) => t.id === flowId);

  // Template path
  if (template) {
    return (
      <FlowProvider flowColor={template.color} flowColorHex={template.colorHex}>
        <ReactFlowProvider>
          <FlowDetailInner templateId={template.id} />
        </ReactFlowProvider>
      </FlowProvider>
    );
  }

  // Saved flow path — requires authentication
  if (flowId) {
    return <RequireAuthForSavedFlow flowId={flowId} />;
  }

  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-muted-foreground">Flow not found</p>
    </div>
  );
};
